import { create } from 'zustand';
import type { ToastMessage } from '@/types';
import { v4 as uuidv4 } from 'uuid';

interface UIState {
  // Modals
  isInfoModalOpen: boolean;
  openInfoModal: () => void;
  closeInfoModal: () => void;

  // Toasts
  toasts: ToastMessage[];
  addToast: (toast: Omit<ToastMessage, 'id'>) => void;
  removeToast: (id: string) => void;
  clearToasts: () => void;

  // Loading
  isLoading: boolean;
  loadingMessage: string;
  setLoading: (isLoading: boolean, message?: string) => void;

  // Bottom navigation
  activeTab: 'ticket' | 'day1' | 'day2';
  setActiveTab: (tab: 'ticket' | 'day1' | 'day2') => void;
}

export const useUIStore = create<UIState>((set) => ({
  // Modals
  isInfoModalOpen: false,
  openInfoModal: () => set({ isInfoModalOpen: true }),
  closeInfoModal: () => set({ isInfoModalOpen: false }),

  // Toasts
  toasts: [],
  addToast: (toast) =>
    set((state) => ({
      toasts: [
        ...state.toasts,
        {
          ...toast,
          id: uuidv4(),
        },
      ],
    })),
  removeToast: (id) =>
    set((state) => ({
      toasts: state.toasts.filter((toast) => toast.id !== id),
    })),
  clearToasts: () => set({ toasts: [] }),

  // Loading
  isLoading: false,
  loadingMessage: 'Loading...',
  setLoading: (isLoading, message = 'Loading...') =>
    set({
      isLoading,
      loadingMessage: message,
    }),

  // Bottom navigation
  activeTab: 'ticket',
  setActiveTab: (tab) => set({ activeTab: tab }),
}));

// Helper hook for toast notifications
export const useToast = () => {
  const { addToast } = useUIStore();

  return {
    success: (message: string, duration?: number) =>
      addToast({ type: 'success', message, duration }),
    error: (message: string, duration?: number) =>
      addToast({ type: 'error', message, duration }),
    warning: (message: string, duration?: number) =>
      addToast({ type: 'warning', message, duration }),
    info: (message: string, duration?: number) =>
      addToast({ type: 'info', message, duration }),
  };
};